import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Building2, ChevronDown, Edit2, FileSpreadsheet, Info, Loader2, MapPin, Plus, PowerOff, Search, Users } from "lucide-react";
import { API, formatApiError } from "../auth/api";
import ImportDialog from "../components/ImportDialog";
import { formatAddress } from "../components/AddressField";
import ExternalCustomers from "./ExternalCustomers";
import { CustomerForm } from "./EconomyPanel";

/**
 * Kunder (2026-10-28) - ét sted for alle kunder og deres steder:
 *  - Egne kunder: fx et byggefirma eller en kæde - deres adresser (byggepladser, butikker) vælges i Ny tur.
 *  - Egne kunder med pladser: genbrugspladser med containere, I vedligeholder (fx ARGO med Pickup).
 *  - Kunder i ContainerFlow: de vedligeholder selv deres pladser - I kører for dem.
 * Før stod egne kunder under Økonomi og kunder med pladser under Lokationer. Priserne pr. kunde er
 * stadig under Økonomi › Kunder & priser; Lokationer er jeres egne steder (depot, modtageanlæg).
 */
const card = "bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-sm";
const input = "w-full px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-900 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/40";
const btnPrimary = "inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold disabled:opacity-50";
const btnGhost = "inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-600 text-sm font-semibold text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700";
const norm = (s) => String(s || "").toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "");
const hit = (q, ...parts) => !q || norm(parts.join(" ")).includes(q);

function OwnCustomers({ q, canEdit, onGoPrices }) {
  const [list, setList] = useState(null);
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState(null);
  const [excel, setExcel] = useState("");   // customers | customer_addresses
  const [open, setOpen] = useState({});
  const load = useCallback(() => {
    axios.get(`${API}/logistics/customers`).then((r) => setList(r.data || [])).catch((e) => { toast.error(formatApiError(e)); setList([]); });
  }, []);
  useEffect(() => { load(); }, [load]);
  if (!list) return <div className={`${card} p-8 flex justify-center`}><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>;
  const shown = list.filter((c) => hit(q, c.name, c.cvr, c.contact, ...(c.addresses || []).map((a) => `${a.label} ${formatAddress(a)}`)));
  const active = shown.filter((c) => c.active !== false);
  const hidden = shown.filter((c) => c.active === false);
  const setActive = async (c, on) => {
    if (!on && !window.confirm(`Skjul ${c.name}? Ture og priser bevares, og kunden kan vises igen.`)) return;
    try {
      if (on) await axios.put(`${API}/logistics/customers/${c.id}`, { name: c.name, active: true });
      else await axios.delete(`${API}/logistics/customers/${c.id}`);
      toast.success(on ? `${c.name} vises igen` : `${c.name} er skjult`); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <section className={card} data-testid="own-customers">
      <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 border-b border-slate-100 dark:border-slate-700">
        <div>
          <h2 className="font-bold text-lg flex items-center gap-2"><Users className="w-5 h-5 text-emerald-600" /> Egne kunder</h2>
          <p className="text-xs text-slate-500">Kunder på egne ture - fx et byggefirma eller en kæde. Deres adresser (byggepladser, butikker, lagre) vælges under
            Afhentning og Aflevering i Ny tur; mange adresser på én gang med Excel. Turen hører til jeres lokation (base).</p>
        </div>
        {canEdit && !adding && <div className="flex flex-wrap gap-2">
          <button className={btnGhost} onClick={() => setExcel("customers")} data-testid="customer-import"><FileSpreadsheet className="w-4 h-4" /> Kunder (Excel)</button>
          <button className={btnGhost} onClick={() => setExcel("customer_addresses")} data-testid="customer-addresses-import"><FileSpreadsheet className="w-4 h-4" /> Adresser (Excel)</button>
          <button className={btnPrimary} onClick={() => setAdding(true)} data-testid="customer-add"><Plus className="w-4 h-4" /> Ny kunde</button>
        </div>}
      </div>
      {excel && <ImportDialog kind={excel} title={excel === "customers" ? "Kunder (Excel)" : "Kundernes adresser (Excel)"} onClose={() => setExcel("")} onDone={load} />}
      {adding && <div className="p-4 border-b border-slate-100 dark:border-slate-700"><CustomerForm onCancel={() => setAdding(false)} onSaved={() => { setAdding(false); load(); }} /></div>}
      {list.length === 0 && !adding && <p className="px-4 py-6 text-sm text-slate-500 text-center">Ingen egne kunder endnu.</p>}
      {list.length > 0 && shown.length === 0 && <p className="px-4 py-6 text-sm text-slate-500 text-center">Ingen egne kunder matcher "{q}".</p>}
      <ul className="divide-y divide-slate-100 dark:divide-slate-700">
        {active.map((c) => {
          const addrs = c.addresses || [];
          const qa = q ? addrs.filter((a) => hit(q, a.label, formatAddress(a))) : addrs;
          return (
            <li key={c.id} className="px-4 py-3" data-testid={`own-customer-${c.name}`}>
              {editing === c.id ? (
                <CustomerForm initial={c} onCancel={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />
              ) : (
                <>
                  <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="font-semibold">{c.name}</div>
                      <div className="text-xs text-slate-500">{[c.cvr && `CVR ${c.cvr}`, c.contact, c.phone, c.email].filter(Boolean).join(" · ") || "Ingen kontaktoplysninger"}</div>
                    </div>
                    <div className="flex flex-wrap items-center gap-1.5 shrink-0">
                      {addrs.length > 0 && (
                        <button className={btnGhost} onClick={() => setOpen({ ...open, [c.id]: !open[c.id] })} data-testid={`own-customer-addrs-${c.name}`}>
                          <MapPin className="w-4 h-4" /> {addrs.length} {addrs.length === 1 ? "adresse" : "adresser"}{q && qa.length !== addrs.length ? ` (${qa.length} matcher)` : ""}
                          <ChevronDown className={`w-4 h-4 transition-transform ${open[c.id] ? "rotate-180" : ""}`} /></button>
                      )}
                      {onGoPrices && <button className={btnGhost} onClick={onGoPrices} title="Kundens priser (Økonomi › Kunder & priser)">Priser</button>}
                      {canEdit && <button className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700" title="Ret kunden og dens adresser" onClick={() => setEditing(c.id)} data-testid={`own-customer-edit-${c.name}`}><Edit2 className="w-4 h-4" /></button>}
                      {canEdit && <button className="p-2 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/30 text-red-600" title="Skjul (ture og priser bevares)" onClick={() => setActive(c, false)}><PowerOff className="w-4 h-4" /></button>}
                    </div>
                  </div>
                  {(open[c.id] || (!!q && qa.length > 0 && qa.length < addrs.length)) && (
                    <ul className="mt-2 ml-1 grid grid-cols-1 md:grid-cols-2 gap-1 text-xs text-slate-600 dark:text-slate-300">
                      {qa.slice(0, 200).map((a) => <li key={a.id || formatAddress(a)} className="truncate"><b>{a.label || a.street}</b> · {formatAddress(a)}</li>)}
                    </ul>
                  )}
                </>
              )}
            </li>
          );
        })}
      </ul>
      {hidden.length > 0 && (
        <div className="px-4 py-3 border-t border-slate-100 dark:border-slate-700 text-xs text-slate-600 dark:text-slate-300 flex flex-wrap items-center gap-1.5">
          <b>Skjulte:</b>
          {hidden.map((c) => (
            <span key={c.id} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-slate-100 dark:bg-slate-700">{c.name}
              {canEdit && <button type="button" className="font-semibold underline" onClick={() => setActive(c, true)}>Vis igen</button>}</span>
          ))}
        </div>
      )}
    </section>
  );
}

/** Kunder i ContainerFlow, I kører for - pr. kunde med deres pladser (de vedligeholder dem selv). */
function PlatformCustomers({ q, pladsList, profile }) {
  const [open, setOpen] = useState({});
  const ownId = profile?.company_id;
  const groups = useMemo(() => {
    const names = Object.fromEntries((profile?.connected_customers || []).map((c) => [c.id, c.name]));
    const m = new Map();
    pladsList.filter((p) => p.company_id && p.company_id !== ownId && !p.customer_external && !p.own_location).forEach((p) => {
      const key = p.company_id;
      if (!m.has(key)) m.set(key, { id: key, name: p.customer_name || names[key] || "Kunde", sites: [] });
      m.get(key).sites.push(p);
    });
    return [...m.values()].sort((a, b) => a.name.localeCompare(b.name, "da"));
  }, [pladsList, profile, ownId]);
  const shown = groups.map((g) => ({ ...g, hits: g.sites.filter((p) => hit(q, g.name, p.name, p.location_code, p.address, p.city)) }))
    .filter((g) => g.hits.length);
  if (!groups.length) return null;
  return (
    <section className={card} data-testid="platform-customers">
      <div className="px-4 py-3 border-b border-slate-100 dark:border-slate-700">
        <h2 className="font-bold text-lg flex items-center gap-2"><Building2 className="w-5 h-5 text-blue-600" /> Kunder i ContainerFlow</h2>
        <p className="text-xs text-slate-500">Kunder, der selv bruger ContainerFlow og har sat jer på som vognmand. De vedligeholder deres pladser, materialer og leveringsadresser - pladserne kommer automatisk med her.</p>
      </div>
      {shown.length === 0 && <p className="px-4 py-6 text-sm text-slate-500 text-center">Ingen matcher "{q}".</p>}
      <ul className="divide-y divide-slate-100 dark:divide-slate-700">
        {shown.map((g) => {
          const isOpen = open[g.id] || !!q;
          return (
            <li key={g.id} className="px-4 py-3" data-testid={`platform-customer-${g.name}`}>
              <button type="button" className="w-full flex items-center gap-2 text-left" onClick={() => setOpen({ ...open, [g.id]: !open[g.id] })}>
                <Building2 className="w-4 h-4 text-blue-600 shrink-0" />
                <span className="font-semibold flex-1">{g.name}</span>
                <span className="text-xs text-slate-500">{g.sites.length} {g.sites.length === 1 ? "plads" : "pladser"}</span>
                <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${isOpen ? "rotate-180" : ""}`} />
              </button>
              {isOpen && (
                <ul className="mt-2 ml-6 grid grid-cols-1 md:grid-cols-2 gap-1 text-xs text-slate-600 dark:text-slate-300">
                  {g.hits.map((p) => <li key={p.id} className="truncate"><b>{p.name}</b>{p.location_code ? ` · ${p.location_code}` : ""}{p.address || p.city ? ` · ${[p.address, p.city].filter(Boolean).join(", ")}` : ""}</li>)}
                </ul>
              )}
            </li>
          );
        })}
      </ul>
    </section>
  );
}

export default function LogisticsCustomers({ profile, pladsList = [], canOwn = true, onGoPrices }) {
  const [q, setQ] = useState("");
  const nq = norm(q.trim());
  return (
    <div className="space-y-5" data-testid="logistics-customers">
      <div className={`${card} p-4 space-y-3`}>
        <div className="flex gap-2 text-sm text-slate-600 dark:text-slate-300">
          <Info className="w-4 h-4 mt-0.5 shrink-0 text-sky-600" />
          <div>Alle jeres kunder og deres steder. <b>Egne kunder</b> har adresser (byggepladser, butikker); <b>kunder med pladser</b> har genbrugspladser med
            containere. Priserne sættes under Økonomi › Kunder & priser, og jeres egne steder (depot, modtageanlæg) under Lokationer.</div>
        </div>
        <div className="relative max-w-md">
          <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input className={`${input} pl-9`} value={q} onChange={(e) => setQ(e.target.value)} placeholder="Søg kunde, plads, adresse eller by" data-testid="customers-search" />
        </div>
      </div>
      {canOwn && <OwnCustomers q={nq} canEdit={canOwn} onGoPrices={onGoPrices} />}
      <ExternalCustomers premium filter={q.trim()} />
      <PlatformCustomers q={nq} pladsList={pladsList} profile={profile} />
    </div>
  );
}
